import React, { useEffect, useMemo, useRef, useState } from "react"

import { httpOk, GET } from "helpers/api"
import { formatMoney } from "helpers/helpers"
import AnalyticsFilterBar, { FilterField, FilterSelect, StandardFields } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { buildPeriod, buildAnalyticsUrl } from "../parts/analyticsDates"

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "12px",
	boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
}

const mutedStyle = {
	color: "var(--dark-text-muted, #8791a1)",
	fontSize: "12px",
}

const MAX_BRANCHES = 4

// highlight — подсвечивать лучшее значение (больше = лучше); hint — подпись под названием строки
const METRICS = [
	{ key: "revenue", label: "Выручка", hint: "за вычетом возвратов", type: "money", highlight: true },
	{ key: "returns", label: "Возвраты", type: "money", highlight: false },
	{ key: "orders", label: "Заказы", type: "int", highlight: true },
	{ key: "average_cheque", label: "Средний чек", type: "money", highlight: true },
	{ key: "revenue_per_hour", label: "Выручка/час", hint: "на рабочий час", type: "money", highlight: true },
	{ key: "orders_per_hour", label: "Заказов/час", hint: "на рабочий час", type: "dec1", highlight: true },
	{ key: "guests_per_cheque", label: "Гостей/чек", type: "dec2", highlight: false },
	{ key: "working_hours", label: "Рабочих часов", type: "dec1", highlight: false },
	{ key: "days_with_data", label: "Дней с данными", type: "int", highlight: false },
]

function formatMetric(value, type) {
	const number = Number(value || 0)
	if (type === "dec1") return number.toFixed(1).replace(".", ",")
	if (type === "dec2") return number.toFixed(2).replace(".", ",")
	return formatMoney(number, 0)
}

function MetricValue({ branch, metric }) {
	const value = formatMetric(branch[metric.key], metric.type)
	if (metric.key !== "working_hours") return value
	return (
		<>
			{value}
			<div style={mutedStyle}>≈ {formatMetric(branch.working_hours_per_day, "dec1")} ч/день</div>
		</>
	)
}

function ComparisonTable({ branches, hoursNote, onRemove }) {
	const headerThStyle = { border: "none", boxShadow: "none", background: "var(--dark-surface, #fff)", fontSize: "13px" }

	const bestIndexByMetric = useMemo(() => {
		const result = {}
		METRICS.forEach(metric => {
			if (!metric.highlight) return
			let bestIndex = -1
			let bestValue = -Infinity
			branches.forEach((branch, index) => {
				const value = Number(branch[metric.key] || 0)
				if (value > bestValue) {
					bestValue = value
					bestIndex = index
				}
			})
			if (bestValue > 0) result[metric.key] = bestIndex
		})
		return result
	}, [branches])

	return (
		<div className="card" style={cardStyle}>
			<div className="card-body">
				<div className="table-responsive">
					<table className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
						<thead>
							<tr style={{ border: "none" }}>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Метрика</th>
								{branches.map(branch => (
									<th className="py-3 text-muted fw-semibold text-center" style={headerThStyle} key={branch.pos_id}>
										{branch.pos_name}
										<i
											className="uil uil-times ms-1"
											style={{ cursor: "pointer" }}
											title="Убрать из сравнения"
											onClick={() => onRemove(branch.pos_id)}
										></i>
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{METRICS.map((metric, rowIndex) => {
								const borderBottom = rowIndex === METRICS.length - 1 ? "none" : "1px solid var(--dark-border, #eef2f6)"
								const bestIndex = bestIndexByMetric[metric.key]
								return (
									<tr key={metric.key}>
										<td className="py-3" style={{ borderBottom, fontWeight: 600, color: "var(--dark-text-strong, #1f2937)" }} title={metric.hint ? hoursNote : undefined}>
											{metric.label}
											{metric.hint && <div style={{ ...mutedStyle, fontWeight: 400 }}>{metric.hint}</div>}
										</td>
										{branches.map((branch, index) => {
											const isBest = metric.highlight && index === bestIndex
											return (
												<td
													className="py-3 text-center"
													style={{
														borderBottom,
														background: isBest ? "var(--dark-surface-3, #e9f7ef)" : "transparent",
														color: isBest ? "var(--dark-fg-success, #1f9d57)" : (metric.highlight ? "var(--dark-text-strong, #1f2937)" : "var(--dark-text-muted, #8791a1)"),
														fontWeight: isBest ? 700 : 500,
													}}
													key={branch.pos_id}
												>
													<MetricValue branch={branch} metric={metric} />
													{isBest && <span className="ms-1">↗</span>}
												</td>
											)
										})}
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
				{hoursNote && <div className="mt-3" style={mutedStyle}>{hoursNote}</div>}
			</div>
		</div>
	)
}

function Comparison() {
	const [data, setData] = useState(null)
	const options = useAnalyticsOptions(["sources"])

	const filters = useAnalyticsFilters({
		...buildPeriod("7days"),
		pos_ids: [],
		order_type_ids: [],
		source_ids: [],
	}, searchWithFilter)
	const { draft, applied, patch, patchApplied } = filters
	const autoSelectedRef = useRef(false)

	async function searchWithFilter(filter) {
		if (!filter.pos_ids.length) {
			setData(null)
			return
		}
		const response = await GET(buildAnalyticsUrl("/analytics/comparison", filter), {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	function removeBranch(posId) {
		patchApplied({ pos_ids: applied.pos_ids.filter(id => id !== posId) })
	}

	function exportExcel() {
		if (!data || !data.branches?.length) return
		const head = `<tr><th>Метрика</th>${data.branches.map(b => `<th>${b.pos_name}</th>`).join("")}</tr>`
		const body = METRICS
			.map(metric => `<tr><td>${metric.label}</td>${data.branches.map(b => `<td>${b[metric.key]}</td>`).join("")}</tr>`)
			.join("")
		const html = `<table border="1"><thead>${head}</thead><tbody>${body}</tbody></table>`
		const blob = new Blob([`﻿${html}`], { type: "application/vnd.ms-excel" })
		const link = document.createElement("a")
		link.href = URL.createObjectURL(blob)
		link.download = `comparison-${data.start_date}_${data.end_date}.xls`
		link.click()
		URL.revokeObjectURL(link.href)
	}

	// При первом открытии подставляем первые два филиала; после ручной очистки не повторяем
	useEffect(() => {
		if (autoSelectedRef.current || !options.poses.length) return
		autoSelectedRef.current = true
		if (!applied.pos_ids.length) patchApplied({ pos_ids: options.poses.slice(0, 2).map(pos => pos.id) })
	}, [options.poses.length]) // eslint-disable-line react-hooks/exhaustive-deps

	const branches = data?.branches || []

	return (
		<>
			<AnalyticsFilterBar
				title="Сравнение филиалов"
				icon="uil-exchange"
				subtitle="Метрики филиалов бок о бок"
				filters={filters}
				presets={["today", "yesterday", "7days", "30days", "this_month", "last_month", "custom"]}
				actions={
					<button className="btn btn-sm btn-outline-secondary" style={{ borderRadius: "10px", fontWeight: 600 }} onClick={exportExcel} disabled={!branches.length}>
						<i className="uil uil-file-export me-1"></i>
						Excel
					</button>
				}
			>
				<FilterField label={`Филиалы (до ${MAX_BRANCHES})`} wide>
					<FilterSelect
						isMulti
						options={options.poses}
						placeholder="Выберите филиалы"
						value={draft.pos_ids}
						onChange={(ids) => patch({ pos_ids: ids.slice(0, MAX_BRANCHES) })}
					/>
				</FilterField>
				<StandardFields filters={filters} options={options} branch={false} />
			</AnalyticsFilterBar>

			{branches.length ? (
				<ComparisonTable branches={branches} hoursNote={data.hours_note} onRemove={removeBranch} />
			) : (
				<div className="card" style={cardStyle}>
					<div className="card-body text-center text-muted py-5">
						{applied.pos_ids.length
							? "Нет данных по выбранным филиалам за период"
							: `Выберите до ${MAX_BRANCHES} филиалов и нажмите «Применить»`}
					</div>
				</div>
			)}
		</>
	)
}

export default Comparison
